<script setup>
import { ref } from 'vue';
import CRResultDetail from './CRResultDetail.vue';
import { isDocumentResult } from '@/utils/documentResultValidator';

const resultDetailRef = ref(null);

const props = defineProps({
  results: Array,
  selectedResult: Object,
  hasManualInputs: Boolean,
  totalSelectedItems: { type: Number, default: 0 },
  applyingBulkDocuments: Boolean,
  enableRegeneration: { type: Boolean, default: true },
  availableOutputsForRegen: Array,
  regeneratingPartial: Boolean,
  isDialog: Boolean,
  isPreviousExecution: Boolean,
  // For doc selection
  getSelectedDocCountFn: Function,
  getSelectedIndicesFn: Function,
  getRowCheckboxStateFn: Function,
  // Selection pass-through
  selectionData: Object,
  selectedItems: Array,
  editedItems: null, // Map instance
  newItems: Array,
  editingItem: [Number, String],
  submittingSelection: Boolean
});

defineEmits([
  'select-result', 'back-to-input', 'apply-bulk',
  'select-all-rows', 'clear-all-rows', 'row-checkbox-click',
  'regenerate', 'retry', 'back-to-results',
  // Selection events (bubble from CRResultDetail)
  'toggle-item', 'select-all', 'clear-all', 'start-edit', 'save-edit',
  'cancel-edit', 'add-new', 'remove-new', 'update-field', 'submit-selection', 'cancel-selection',
  // Document events
  'expand-all-doc', 'collapse-all-doc', 'select-all-doc', 'clear-all-doc',
  'doc-selection-changed'
]);

defineExpose({
  getDocumentRef(rowId) {
    return resultDetailRef.value?.getDocumentRef(rowId) || null;
  }
});
</script>

<template>
  <div class="cr-results-multi">
    <!-- Header -->
    <div class="results-header">
      <div class="results-header-left">
        <div class="results-header-icon">
          <v-icon color="white">mdi-check-circle</v-icon>
        </div>
        <div>
          <h3 class="results-header-title">Content Generation Results</h3>
          <p class="results-header-subtitle">
            {{ results.length }} execution{{ results.length > 1 ? 's' : '' }} completed
          </p>
        </div>
      </div>
      <div class="results-header-actions">
        <!-- Bulk Document Actions -->
        <div v-if="results.some(r => isDocumentResult(r))" class="bulk-document-actions">
          <div class="bulk-selection-badge">
            <v-icon small class="mr-1">mdi-checkbox-multiple-marked</v-icon>
            <span v-if="totalSelectedItems > 0">{{ totalSelectedItems }} items selected</span>
            <span v-else class="no-selection">No items selected</span>
          </div>
          <v-btn small class="header-action-btn" @click="$emit('select-all-rows')" :disabled="applyingBulkDocuments">
            <v-icon small left>mdi-select-all</v-icon>
            Select All
          </v-btn>
          <v-btn small class="header-action-btn" @click="$emit('clear-all-rows')" :disabled="applyingBulkDocuments || totalSelectedItems === 0">
            <v-icon small left>mdi-close-box-outline</v-icon>
            Clear All
          </v-btn>
          <v-btn
            small
            class="header-action-btn-primary"
            @click="$emit('apply-bulk')"
            :loading="applyingBulkDocuments"
            :disabled="totalSelectedItems === 0"
          >
            <v-icon small left>mdi-check</v-icon>
            Apply ({{ totalSelectedItems }})
          </v-btn>
        </div>

        <v-btn v-if="hasManualInputs" class="back-btn" text @click="$emit('back-to-input')">
          <v-icon left>mdi-arrow-left</v-icon>
          Back to Input Form
        </v-btn>
      </div>
    </div>

    <!-- Split View -->
    <div class="results-split-view">
      <!-- Left Panel: Results List -->
      <div class="results-list-panel">
        <div class="results-list-header">
          <v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
          <span>All Results</span>
          <span class="results-count-badge">{{ results.length }}</span>
        </div>
        <div class="results-list-content">
          <div
            v-for="result in results"
            :key="result.rowId"
            @click="$emit('select-result', result)"
            :class="['result-item', {
              'active': selectedResult?.rowId === result.rowId,
              'processing': result.processing,
              'result-success': !result.processing && result.success,
              'result-error': !result.processing && !result.success
            }]"
          >
            <!-- Row Selection Checkbox -->
            <div v-if="isDocumentResult(result) && getRowCheckboxStateFn" class="result-row-selection">
              <v-checkbox
                :input-value="getRowCheckboxStateFn(result.rowId).checked"
                :indeterminate="getRowCheckboxStateFn(result.rowId).indeterminate"
                @click.stop
                @change="$emit('row-checkbox-click', result.rowId)"
                :disabled="applyingBulkDocuments"
                hide-details
                dense
                color="primary"
                class="row-selection-checkbox"
              ></v-checkbox>
            </div>

            <div class="result-item-icon">
              <div v-if="result.processing" class="processing-indicator">
                <v-progress-circular
                  :value="result.progress || 0"
                  :indeterminate="!result.progress"
                  color="primary"
                  size="28"
                  width="3"
                >
                  <span v-if="result.progress" class="progress-text">{{ result.progress }}</span>
                </v-progress-circular>
              </div>
              <div v-else-if="result.success" class="status-icon result-success">
                <v-icon size="18" color="white">mdi-check</v-icon>
              </div>
              <div v-else class="status-icon error">
                <v-icon size="18" color="white">mdi-alert</v-icon>
              </div>
            </div>

            <div class="result-item-content">
              <div class="result-item-header">
                <span class="result-item-title">Row {{ result.rowNumber }}</span>
                <v-chip v-if="isDocumentResult(result)" x-small class="doc-badge">DOC</v-chip>
              </div>
              <span v-if="result.chainName" class="chain-name">{{ result.chainName }}</span>

              <div v-if="result.processing && result.progress" class="result-progress-bar">
                <div class="progress-fill" :style="{ width: result.progress + '%' }"></div>
              </div>

              <div class="result-item-meta">
                <template v-if="result.processing">
                  <span class="row-status-text row-processing">{{ result.currentStep || 'Processing...' }}</span>
                  <div v-if="result.progressDetails" class="step-info">
                    {{ Math.min(result.progressDetails.completed + 1, result.progressDetails.total) }}/{{ result.progressDetails.total }} steps
                    <span v-if="result.progressDetails.failed > 0" class="failed-count">({{ result.progressDetails.failed }} failed)</span>
                  </div>
                </template>
                <template v-else-if="result.success">
                  <span class="row-status-text row-success">Completed</span>
                  <div v-if="result.executionDetails" class="step-info">
                    {{ result.executionDetails.completed_steps }}/{{ result.executionDetails.total_steps }} steps
                  </div>
                </template>
                <template v-else>
                  <span class="row-status-text row-error">Failed</span>
                  <div v-if="result.executionDetails" class="step-info">
                    {{ result.executionDetails.completed_steps }}/{{ result.executionDetails.total_steps }} steps
                  </div>
                </template>
              </div>
            </div>

            <div class="result-item-arrow">
              <v-icon small>mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel: Result Details -->
      <div class="result-detail-panel">
        <CRResultDetail
          ref="resultDetailRef"
          v-if="selectedResult"
          :result="selectedResult"
          :is-dialog="isDialog"
          :enable-regeneration="enableRegeneration"
          :available-outputs-for-regen="availableOutputsForRegen"
          :regenerating-partial="regeneratingPartial"
          :is-previous-execution="isPreviousExecution"
          :selected-doc-count="getSelectedDocCountFn ? getSelectedDocCountFn(selectedResult.rowId) : 0"
          :selected-indices="getSelectedIndicesFn ? getSelectedIndicesFn(selectedResult.rowId) : null"
          :applying-documents="applyingBulkDocuments"
          :selection-data="selectionData"
          :selected-items="selectedItems"
          :edited-items="editedItems"
          :new-items="newItems"
          :editing-item="editingItem"
          :submitting-selection="submittingSelection"
          @regenerate="(r, key) => $emit('regenerate', r, key)"
          @retry="(r) => $emit('regenerate', r, null)"
          @back-to-results="$emit('back-to-results')"
          @toggle-item="$emit('toggle-item', $event)"
          @select-all="$emit('select-all')"
          @clear-all="$emit('clear-all')"
          @start-edit="$emit('start-edit', $event)"
          @save-edit="$emit('save-edit', $event)"
          @cancel-edit="$emit('cancel-edit', $event)"
          @add-new="$emit('add-new')"
          @remove-new="$emit('remove-new', $event)"
          @update-field="(index, field, value) => $emit('update-field', index, field, value)"
          @submit-selection="$emit('submit-selection')"
          @cancel-selection="$emit('cancel-selection')"
          @expand-all-doc="$emit('expand-all-doc')"
          @collapse-all-doc="$emit('collapse-all-doc')"
          @select-all-doc="$emit('select-all-doc')"
          @clear-all-doc="$emit('clear-all-doc')"
          @doc-selection-changed="(rowId, data) => $emit('doc-selection-changed', rowId, data)"
        />
        <div v-else class="no-selection-placeholder">
          <v-icon size="48" color="grey lighten-1">mdi-cursor-default-click</v-icon>
          <p class="mt-2 grey--text">Select a result to view details</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cr-results-multi {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
  overflow: hidden;
}
.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  border-radius: 8px 8px 0 0;
  color: white;
  flex-shrink: 0;
}
.results-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.results-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.results-header-title {
  margin: 0;
  font-size: 16px;
}
.results-header-subtitle {
  margin: 0;
  font-size: 12px;
  opacity: 0.8;
}
.results-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bulk-document-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.bulk-selection-badge {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  font-size: 12px;
}
.header-action-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
}
.header-action-btn-primary {
  background: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
}
.results-split-view {
  display: flex;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 8px 8px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.results-list-panel {
  width: 300px;
  min-width: 300px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
}
.results-list-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 13px;
}
.results-count-badge {
  margin-left: auto;
  padding: 1px 8px;
  background: #e3f2fd;
  color: #1976D2;
  border-radius: 10px;
  font-size: 11px;
}
.results-list-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.result-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}
.result-item:hover {
  background: #f5f5f5;
}
.result-item.active {
  background: #e3f2fd;
  border-left: 3px solid #1976D2;
}
.result-row-selection {
  flex-shrink: 0;
  margin-right: 4px;
}
.result-item-icon {
  flex-shrink: 0;
  margin-right: 12px;
}
.status-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.status-icon.result-success { background: #4CAF50; }
.status-icon.result-error { background: #f44336; }
.result-item-content {
  flex-grow: 1;
  min-width: 0;
}
.result-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
}
.result-item-title {
  font-weight: 600;
  font-size: 13px;
}
.chain-name {
  font-size: 11px;
  color: #666;
  display: block;
}
.result-progress-bar {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: #1976D2;
  transition: width 0.3s;
}
.result-item-meta {
  margin-top: 4px;
}
.row-status-text {
  font-size: 11px;
}
.row-processing { color: #1976D2; }
.row-success { color: #4CAF50; }
.row-error { color: #f44336; }
.step-info {
  font-size: 10px;
  color: #999;
}
.failed-count { color: #f44336; }
.result-item-arrow {
  flex-shrink: 0;
  margin-left: 8px;
}
.result-detail-panel {
  flex: 1;
  min-width: 0;
  padding: 16px;
  overflow-y: auto;
  background: white;
}
.no-selection-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
}
.progress-text {
  font-size: 9px;
}
</style>
